// © Microsoft Corporation. All rights reserved.

// Usage:
//              pass data to any callback that has subscribed to the event
//              mediator.pub(event, data);
//
//              call the callback whenever this event is published
//              mediator.sub(event, callback)
//
//              pass data to any callback that has subscribed to this event and channel
//              mediator.pubChannel(event, channel, data);
//
//              call the callback whenever this event is published to this channel
//              mediator.subChannel(event, channel, callback)
//
//              Think of pub/sub as using their own channel, useful for 1 ofs or app communication.
//              Channels are useful when multiple copies of the same widget want to communicate.

/// <summary>Constructor for mediator object</summary>
/// <returns type="Object">Mediator object</returns>

/// <var type="object">Default publish subscribe where all non-channel-specific events are managed</var>
// @ts-ignore
var defaultPubSub = new PublishSubscribe();
var channels = {};
export default {
    pub: defaultPubSub.pub,
    sub: defaultPubSub.sub,
    unsub: defaultPubSub.unsub,
    pubChannel: function (event, channel, data) {
        /// <summary>Publishes event to channel subscribers only</summary>
        /// <param name="event" type="string">Event to subscribe to</param>
        /// <param name="channel" type="string">Name of the channel</param>
        /// <param name="data">Data to pass to the callback</param>
        getChannel(channel).pub(event, data);
    },
    subChannel: function (event, channel, callback) {
        /// <summary>Subscribes to event published on a specific channel</summary>
        /// <param name="event" type="string">Event to subscribe to</param>
        /// <param name="channel" type="string">Name of the channel</param>
        /// <param name="callback">Function to call when the event is fired</param>
        getChannel(channel).sub(event, callback);
    },
    unsubChannel: function (event, channel, callback) {
        /// <summary>Unsubscribe from an event published on a specific channel</summary>
        /// <param name="event" type="string">Event to subscribe to</param>
        /// <param name="channel" type="string">Name of the channel</param>
        /// <param name="callback">Function to call when the event is fired</param>
        getChannel(channel).unsub(event, callback);
    }
};

function getChannel(channel) {
    /// <summary>Gets a channel from the collection of mediator channels</summary>
    /// <param name="channel">ID of the channel</param>
    /// <returns type="object">Channel requested or new channel if not exist</returns>
    if (!channels[channel]) {
        // @ts-ignore
        channels[channel] = new PublishSubscribe();
    }
    return channels[channel];
}

function PublishSubscribe() {
    /// <summary>PublishSubscribe factory</summary>
    /// <returns type="object">PubSub Object</returns>
    var pubMap = {};
    return {
        pub: function (event, data) {
            /// <summary>Passes data to any callback that has subscribed to the event</summary>
            /// <param name="event">Event being published</param>
            /// <param name="data">Data to pass to the callback</param>
            var callbacks = pubMap[event];

            if (callbacks) {
                for (var ndx = 0; ndx < callbacks.length; ndx++) {
                    callbacks[ndx](data);
                }
            }
        },
        sub: function (event, callback) {
            /// <summary>Subscribes the callback to this event</summary>
            /// <param name="event">Event to subscribe to</param>
            /// <param name="callback">Function to call when the event is fired</param>
            if (typeof callback == "function") {
                var listeners = pubMap[event];

                if (!listeners) {
                    listeners = [];
                    pubMap[event] = listeners;
                }
                listeners.push(callback);
            }
        },
        unsub: function (event, callback) {
            /// <summary>Unsubscribes a callback from an event</summary>
            /// <param name="event">Event to unsubscribe from</param>
            /// <param name="callback">Function to unsubscribe from the event</param>
            var listeners = pubMap[event];

            if (listeners) {
                for (var ndx = 0; ndx < listeners.length; ndx++) {
                    if (listeners[ndx] === callback) {
                        listeners.splice(ndx--, 1);
                    }
                }
            }
        }
    };
}
